import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Crosshair, CurrencyCircleDollar } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_LABEL = { apac: "APAC", europe: "Europe" };

export default function CompetitivePage() {
  const { productId } = useProduct();
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    axios.get(`${API}/competitive/${productId}`).then((r) => setD(r.data)).catch(console.error);
  }, [productId]);

  if (!d) return (
    <PageShell pageId="competitive" overline="Competitive Intelligence Layer" title="Global Competitive Intelligence">
      <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Loading competitive intelligence…</div>
    </PageShell>
  );

  const profiled = d.markets.filter((m) => m.competitor_count > 0);
  return (
    <PageShell pageId="competitive" overline={`Competitive Intelligence Layer · ${d.product.name}`} title="Global Competitive Intelligence">
      {profiled.length === 0 ? (
        <div data-testid="competitive-empty" className="py-32 text-center">
          <Crosshair size={40} className="mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-4">No competitive intelligence recorded yet for {d.product.name}.</p>
          <p className="text-xs text-slate-400 mt-1">Competitor presence is captured per market on the product's country overlays.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div data-testid="competitive-summary" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 border border-slate-200 bg-white divide-x divide-y md:divide-y-0 divide-slate-200">
            {[["Markets Profiled", `${d.summary.markets_profiled}/${d.summary.total_markets}`],
              ["Competitor Devices", d.summary.unique_competitors],
              ["Companies", d.summary.companies],
              ["Pricing Anchors", d.summary.pricing_anchors],
              ["Whitespace Markets", d.summary.whitespace_markets.length]].map(([l, v]) => (
              <div key={l} className="p-5"><p className="overline-label text-slate-400">{l}</p><p className="font-heading font-extrabold text-2xl mt-2">{v}</p></div>
            ))}
          </div>

          <section data-testid="competitive-players" className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <p className="font-heading font-bold text-sm tracking-tight">Global Player Footprint</p>
              <p className="text-xs text-slate-500 mt-1">Where each company holds market presence — pricing-anchor markets set the reference price {d.product.name} is negotiated against.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {d.players.map((p) => (
                <div key={p.company} data-testid={`player-${p.company.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="w-56 shrink-0">
                    <p className="text-sm font-semibold text-slate-900">{p.company}</p>
                    <p className="font-mono-data text-[10px] text-slate-400">{p.market_count} market(s)</p>
                  </div>
                  <div className="flex-1 h-1.5 bg-slate-100 min-w-[120px] max-w-xs"><div className="h-full bg-slate-900" style={{ width: `${Math.round(100 * p.market_count / d.summary.total_markets)}%` }} /></div>
                  <div className="flex flex-wrap gap-1">
                    {p.pricing_anchor_in.map((m) => (
                      <span key={m} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                        <CurrencyCircleDollar size={10} weight="bold" /> anchor · {m}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {["apac", "europe"].map((region) => {
            const regionMarkets = profiled.filter((m) => m.region === region);
            if (!regionMarkets.length) return null;
            return (
              <section key={region} data-testid={`competitive-region-${region}`}>
                <p className="overline-label text-slate-400">{REGION_LABEL[region] || region}</p>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-3">
                  {regionMarkets.map((m) => (
                    <div key={m.id} data-testid={`competitive-market-${m.id}`} className="bg-white border border-slate-200">
                      <div className="px-5 py-3.5 border-b border-slate-100 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{m.flag} {m.name}</p>
                          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{m.note}</p>
                        </div>
                        {m.whitespace && <span className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white">Whitespace</span>}
                      </div>
                      <div className="divide-y divide-slate-50">
                        {m.competitors.map((c, i) => (
                          <div key={i} className="px-5 py-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-slate-800">{c.competitor} <span className="text-slate-400 font-normal">· {c.company}</span></p>
                              {c.pricing_anchor && <span className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">Pricing anchor</span>}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">{c.status}</p>
                            <p className="text-[11px] text-slate-600 mt-1.5 pl-2 border-l-2 border-slate-200 leading-relaxed"><span className="font-semibold text-slate-500">Precedent:</span> {c.precedent}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
